import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Gem, Pin, PinOff, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { navForRole, isSectionActive, activeLeaf, activeSubLeaf, subnavPageCount, ONBOARDING_NAV, sectionLabel, type NavItem } from './nav-config'
import { useOnboardingEntry } from './onboarding-entry'
import { ProgressRing } from './progress-ring'
import { NotificationsBell } from './notifications-bell'
import { UserMenu } from './user-menu'
import { BrandLogo } from './brand-logo'
import { BugIcon } from './bug-icon'
import { FeedbackDialog } from './feedback-bubble'
import { OPEN_FEEDBACK_EVENT } from './command-items'
import { MOD_KEY_LABEL } from '@/lib/command-search'
import { useUiStore } from '@/stores/ui-store'
import { useAuthStore } from '@/stores/auth-store'
import { useMyPermissions } from '@/hooks/use-settings'
import { cn } from '@/lib/utils'
import { resolvePlan } from '@/pages/billing/plans'
import { usePendingOrder } from '@/pages/billing/pending-order-bar'

/**
 * Menu chính kiểu SaleSmartly: cột icon hẹp, rê chuột thì bung rộng đè lên nội
 * dung (không đẩy trang); bấm ghim để giữ mở và chiếm chỗ cố định. Mục có trang
 * con hiện mũi tên và bật bảng trang con sang phải khi rê vào.
 *
 * Toàn bộ dùng CSS hover — không có state mở/đóng nên không kẹt menu khi chuột
 * rời cửa sổ đột ngột.
 */
export function SideNav({ forceCollapsed = false }: { forceCollapsed?: boolean }) {
  const { sidebarCollapsed, toggleSidebar } = useUiStore()
  // Đăng ký đổi ngôn ngữ: nhãn menu (navForRole) dịch lúc vẽ.
  const { t } = useTranslation('shell')
  const role = useAuthStore((s) => s.user?.role)
  const { data: perms } = useMyPermissions()
  const nav = navForRole(role, perms)
  const { pathname } = useLocation()
  // Ở Hội thoại luôn chỉ chiếm bề rộng cột icon để nhường chỗ cho khung chat,
  // nhưng KHÔNG ghi đè lựa chọn ghim đã lưu — rời Hội thoại là trở lại như cũ.
  const pinned = !sidebarCollapsed && !forceCollapsed

  return (
    <aside className={cn('relative z-30 h-full shrink-0 transition-[width] duration-200', pinned ? 'w-52' : 'w-[60px]')}>
      <div
        className={cn(
          'group/rail absolute left-0 top-0 flex min-h-full flex-col bg-rail text-sidebar-foreground transition-[width,box-shadow] duration-200',
          pinned
            ? 'w-52'
            : 'w-[60px] hover:w-52 hover:rounded-r-xl hover:shadow-[4px_0_24px_-6px_rgb(0_0_0/0.18)]',
        )}
      >
        <div className="flex h-14 shrink-0 items-center gap-2 overflow-hidden px-3">
          {/* Cột hẹp: biểu tượng chữ "E". Bung ra/ghim: logo đầy đủ thay chỗ. */}
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center',
              pinned ? 'hidden' : 'group-hover/rail:hidden',
            )}
          >
            <img src="/assets/logo-mark.png" alt="eCDP" className="h-7 w-7 object-contain" />
          </span>
          <div className={cn('min-w-0 flex-1', pinned ? 'block' : 'hidden group-hover/rail:block')}>
            <BrandLogo variant="menu" />
          </div>
          {!forceCollapsed && (
            <button
              type="button"
              onClick={toggleSidebar}
              title={pinned ? t('rail.unpin') : t('rail.pin')}
              className={cn(
                REVEAL,
                pinned && 'opacity-100',
                'shrink-0 rounded-md p-1.5 text-sidebar-foreground/60 hover:bg-white/60 hover:text-primary',
              )}
            >
              {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
          )}
        </div>

        <nav className="flex-1 space-y-1 px-2 py-1">
          <SearchRailItem pinned={pinned} />
          <div className="mx-2 border-t border-sidebar-foreground/10" />
          <OnboardingRailItem pathname={pathname} pinned={pinned} />
          {nav.map((item) => (
            <RailItem
              key={item.key}
              item={item}
              pathname={pathname}
              pinned={pinned}
            />
          ))}
        </nav>

        <div className="space-y-1 px-2 py-2">
          <FeedbackItem pathname={pathname} pinned={pinned} />
          <PlanItem pathname={pathname} pinned={pinned} />
          <NotificationsBell
            className={cn(ITEM, IDLE, 'justify-start font-normal hover:bg-white/55 [&_svg]:size-5')}
            label={<span className={cn(REVEAL, pinned && 'opacity-100')}>{t('rail.notifications')}</span>}
          />
          <UserMenu labelClassName={cn(REVEAL, pinned && 'opacity-100')} />
        </div>
      </div>

    </aside>
  )
}

// Chữ trong cột: ẩn khi cột hẹp, hiện khi bung (rê chuột) hoặc đã ghim.
const REVEAL = 'whitespace-nowrap opacity-0 transition-opacity duration-150 group-hover/rail:opacity-100'
const ITEM = 'flex h-10 w-full items-center gap-3 overflow-hidden rounded-lg px-3 text-sm transition-colors'
const ACTIVE = 'bg-white font-medium text-primary shadow-sm dark:bg-white/10'
const IDLE = 'text-sidebar-foreground/80 hover:bg-white/55 hover:text-sidebar-foreground dark:hover:bg-white/5'

/** Kính lúp đầu menu: mở bảng lệnh ⌘K (command-palette.tsx) — tìm trang, mục cài đặt, thao tác. */
function SearchRailItem({ pinned }: { pinned: boolean }) {
  const setCommandOpen = useUiStore((s) => s.setCommandOpen)
  const { t } = useTranslation('shell')
  return (
    <button
      type="button"
      onClick={() => setCommandOpen(true)}
      aria-label={t('rail.searchAria', { key: `${MOD_KEY_LABEL} K` })}
      aria-keyshortcuts="Meta+K Control+K"
      title={t('rail.searchTitle', { key: `${MOD_KEY_LABEL} K` })}
      className={cn(ITEM, IDLE)}
    >
      <Search className="h-5 w-5 shrink-0" strokeWidth={1.8} />
      <span className={cn(REVEAL, pinned && 'opacity-100', 'min-w-0 flex-1 truncate text-left')}>{t('rail.search')}</span>
      <kbd className={cn(REVEAL, pinned && 'opacity-100', 'shrink-0 rounded border border-sidebar-foreground/20 px-1.5 font-sans text-[11px] text-sidebar-foreground/60')}>
        {MOD_KEY_LABEL} K
      </kbd>
    </button>
  )
}

/** Mục "Bắt đầu" đầu menu (chủ tổ chức mới, tới khi xong / bỏ qua) — vòng tiến độ thay icon khi cột hẹp. */
function OnboardingRailItem({ pathname, pinned }: { pathname: string; pinned: boolean }) {
  const { show, steps } = useOnboardingEntry()
  const { t } = useTranslation('shell')
  if (!show) return null
  const isActive = pathname === ONBOARDING_NAV.to
  const label = sectionLabel(ONBOARDING_NAV)
  return (
    <Link
      to={ONBOARDING_NAV.to}
      aria-current={isActive ? 'page' : undefined}
      aria-label={`${label} (${steps.doneCount}/${steps.total})`}
      title={t('rail.onboardingTitle', { label, done: steps.doneCount, total: steps.total })}
      className={cn(ITEM, 'px-2.5', isActive ? ACTIVE : IDLE)}
    >
      <ProgressRing value={steps.doneCount} total={steps.total} size={26} />
      <span className={cn(REVEAL, pinned && 'opacity-100', 'min-w-0 flex-1 truncate')}>{label}</span>
      <span
        className={cn(REVEAL, pinned && 'opacity-100', 'shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-xs tabular-nums text-primary')}
      >
        {steps.doneCount}/{steps.total}
      </span>
    </Link>
  )
}

/** Gửi Feedback cố định ở đáy menu: ghim / bung → nút rộng cả cột có chữ; thu gọn → chỉ icon con bọ. Mở hộp góp ý (báo lỗi / góp ý / order tính năng). */
function FeedbackItem({ pathname, pinned }: { pathname: string; pinned: boolean }) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('shell')
  // Bảng lệnh ⌘K có thao tác "Gửi Feedback" → mở đúng hộp này.
  useEffect(() => {
    const show = () => setOpen(true)
    window.addEventListener(OPEN_FEEDBACK_EVENT, show)
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, show)
  }, [])
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('rail.feedbackAria')}
        title={t('rail.feedback')}
        className={cn(ITEM, 'mb-1 bg-primary font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40')}
      >
        <BugIcon className="h-5 w-5 shrink-0" />
        <span className={cn(REVEAL, pinned && 'opacity-100', 'min-w-0 flex-1 truncate text-left')}>{t('rail.feedback')}</span>
      </button>
      <FeedbackDialog open={open} onOpenChange={setOpen} pathname={pathname} />
    </>
  )
}

/** Lối vào "Gói dịch vụ" ở đáy cột (kiểu SaleSmartly) — bung ra thì hiện tên gói. */
function PlanItem({ pathname, pinned }: { pathname: string; pinned: boolean }) {
  const planLabel = useAuthStore((s) => s.user?.org?.plan)
  const isActive = pathname.startsWith('/billing')
  // Có đơn thanh toán đang chờ / vừa hết hạn → chấm cam ở biểu tượng (khách ẩn thanh nhắc vẫn tìm lại được đơn).
  const { pending, lapsed } = usePendingOrder()
  const hasOrder = !!(pending || lapsed)
  const { t } = useTranslation('shell')
  const title = hasOrder ? t('rail.planPending') : t('rail.plan')
  return (
    <Link
      to="/billing"
      aria-current={isActive ? 'page' : undefined}
      aria-label={title}
      title={title}
      className={cn(ITEM, isActive ? ACTIVE : IDLE)}
    >
      <span className="relative shrink-0">
        <Gem className="h-5 w-5" strokeWidth={isActive ? 2.2 : 1.8} />
        {hasOrder && <span className={cn('absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-2 ring-sidebar', lapsed && !pending ? 'bg-destructive' : 'bg-warning')} />}
      </span>
      <span className={cn(REVEAL, pinned && 'opacity-100', 'min-w-0 flex-1 truncate')}>{t('rail.plan')}</span>
      <span
        className={cn(
          REVEAL,
          pinned && 'opacity-100',
          'max-w-20 shrink-0 truncate rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary',
        )}
      >
        {resolvePlan(planLabel).name}
      </span>
    </Link>
  )
}

function RailItem({ item, pathname, pinned }: { item: NavItem; pathname: string; pinned: boolean }) {
  const Icon = item.icon
  const isActive = isSectionActive(item, pathname)
  const current = activeLeaf(item, pathname)
  const sub = current ? activeSubLeaf(current, pathname) : undefined
  // Đếm cả trang cấp 3: vai trò chỉ thấy "Nhân viên AI" (một mục cấp 2 có 5–6 trang con) vẫn cần bảng bật.
  const hasFlyout = subnavPageCount(item) > 1

  return (
    <div className="group/item relative">
      <Link
        to={item.to}
        aria-current={isActive ? 'page' : undefined}
        aria-label={item.label}
        className={cn(ITEM, isActive ? ACTIVE : IDLE)}
      >
        <Icon className="h-5 w-5 shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
        <span className={cn(REVEAL, pinned && 'opacity-100', 'min-w-0 flex-1 truncate')}>{item.label}</span>
        {hasFlyout && (
          <ChevronRight className={cn(REVEAL, pinned && 'opacity-100', 'h-4 w-4 shrink-0 text-sidebar-foreground/50')} />
        )}
      </Link>

      {hasFlyout && (
        // pl-2 làm "cầu nối" vô hình giữa mục và bảng — chuột đi ngang không bị mất hover.
        <div className="invisible absolute left-full top-0 z-50 pl-2 opacity-0 transition-opacity duration-100 group-hover/item:visible group-hover/item:opacity-100">
          <div className="w-60 rounded-xl border bg-popover p-2 text-popover-foreground shadow-lg">
            <div className="flex items-center gap-2 px-2 pb-2 pt-1 text-sm font-medium text-primary">
              <Icon className="h-4 w-4" /> {item.label}
            </div>
            <div className="space-y-0.5 rounded-lg bg-muted/60 p-1">
              {item.children!.map((c) => (
                <div key={c.to}>
                  <Link
                    to={c.to}
                    className={cn(
                      'block rounded-md px-3 py-2 text-sm transition-colors',
                      c === current && !sub ? 'bg-card font-medium text-primary' : c === current ? 'font-medium text-primary hover:bg-card' : 'hover:bg-card',
                    )}
                  >
                    {c.label}
                  </Link>
                  {/* Trang cấp 3 (Nhân viên AI › …) thụt vào dưới trang cha. */}
                  {c.children && (
                    <div className="mb-1 ml-3 space-y-0.5 border-l border-border pl-2">
                      {c.children.map((g) => (
                        <Link
                          key={g.to}
                          to={g.to}
                          className={cn(
                            'block rounded-md px-3 py-1.5 text-sm transition-colors',
                            g === sub ? 'bg-card font-medium text-primary' : 'text-foreground/85 hover:bg-card',
                          )}
                        >
                          {g.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
